import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';

/**
 * Wraps a higher order functional component with additional modifications needed.
 * See https://reactjs.org/docs/higher-order-components.html
 *
 * @param HigherOrderComponent
 * @param Component
 * @param higherOrderComponentName Name of higher order component for modifying displayName to assist debugging
 * @template P The Props type of the original component
 * @template T The Props type of the higher order component (P by default)
 * @returns
 */
export const wrapHigherOrderComponent = <P, T = P>(
  HigherOrderComponent: React.FC<T>,
  Component: React.ComponentType<P>,
  higherOrderComponentName: string
): React.FC<T> => {
  hoistNonReactStatics(HigherOrderComponent, Component);

  // Improve name in React debugging extension
  HigherOrderComponent.displayName = `${higherOrderComponentName}(${getDisplayName(Component)})`;

  return HigherOrderComponent;
};

const getDisplayName = (Component: React.ComponentType<any>) => Component.displayName || Component.name || 'Component';

export const recursiveMap = (children: React.ReactNode, fn: (c: React.ReactElement) => void) => {
  return React.Children.map(children, (child: any) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    if ((child as React.ReactElement).props.children) {
      var children = recursiveMap((child as React.ReactElement).props.children, fn);
      // When we have a singular child element retrieve it directly. The reason is that the children property is an element
      // itself when it "contains" a singular child. Additionally react functions (such as React.Children.only) do not consider
      // an array of one element to be the same as a singular JSX element.
      const props = {
        children: children?.length === 1 ? children[0] : children,
      };
      child = React.cloneElement(child, props);
    }

    return fn(child);
  });
};
